/* eslint-disable @typescript-eslint/no-use-before-define, no-param-reassign -- the simulation is a set of mutually recursive steps (winner → re-aim → motion → winner) ported as hoisted functions, over mutable model objects (routes, packets) the engine owns */
/**
 * Multiconexão simulation engine (ported from the design's `app.js` §3, `bootMulticonexao`).
 *
 * React renders the card's markup (the five route rows in their baseline state, the empty wires SVG and the
 * empty packet layer); this module hydrates it in place: it picks the routes for the visitor's location,
 * runs the live pings (every 0.5 s: the winner at 2–5 ms, the direct line at 180–250 ms), the 5.2 s route
 * handovers, the retest / pause / focus / pin interactions, and flies the packets along SVG wires measured
 * from the layout (Web Animations). It owns only what it creates (every child of `[data-wires]` and
 * `[data-packets]`) plus classes, attributes and text of React nodes whose props never change.
 *
 * `startMulticonexao` returns a destroy function that stops every timer, frame, observer, listener and
 * animation and removes what it built, so React StrictMode's mount → cleanup → mount is safe.
 */
import type { GeoCity, GeoData, McPlace } from '../geo/geo';
import { GEO_ENDPOINT, MC_PLACE_EVENT, sharedGeo, writeMcPlace } from '../geo/geo';

export interface McStrings {
  /** the visitor's side label when the city is unknown ("sua internet") */
  pcPlaceDefault: string;
  gamePlace: (city: string) => string;
  /** sr-only description of the diagram with the five route cities and the game server city */
  desc: (routes: string[], hub: string) => string;
  deltaActive: string;
  delta: (ms: number) => string;
  routeLabelWinner: (city: string, country: string, ms: number) => string;
  routeLabel: (city: string, country: string, ms: number, delta: number) => string;
  routeLabelMeasuring: (city: string, country: string) => string;
  directLabel: (ms: number) => string;
  directLabelMeasuring: string;
  proofSr: (bad: number, good: number, pct: number) => string;
  /** the status line strings return HTML: the city names arrive escaped, the figures as live `<span>`s */
  statusChanged: (previousCity: string, previousMs: string, bestCity: string, bestMs: string) => string;
  statusMeasured: (bestCity: string, bestMs: string) => string;
  statusLeading: (bestCity: string, bestMs: string) => string;
  statusPaused: (bestCity: string, bestMs: string) => string;
  statusFocusWinner: (city: string, ms: string) => string;
  statusFocusNext: (city: string, delta: string, bestCity: string) => string;
  statusFocusReserve: (city: string, delta: string, bestCity: string) => string;
  statusMeasuring: string;
  liveMeasuring: string;
  liveMeasured: (city: string, ms: number, pct: number) => string;
  liveMeasuredChanged: (city: string, ms: number, previousCity: string, pct: number) => string;
  livePinnedWinner: (city: string, ms: number) => string;
  livePinned: (city: string, ms: number, delta: number, bestCity: string) => string;
  liveCleared: string;
  livePaused: string;
  liveResumed: string;
  buttonIdle: string;
  buttonBusy: string;
}

interface City {
  city: string;
  code: string;
  lat: number;
  lon: number;
  cc: string;
}

interface Geo {
  country: string;
  region: string;
  city: string;
  lat: number | null;
  lon: number | null;
}

type Scenario = Record<string, number>;

interface Place {
  key: string;
  cc: string;
  hub: City;
  user: City | null;
  routes: City[];
  scenarios: Scenario[];
}

interface Box {
  l: number;
  t: number;
  r: number;
  b: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

interface Packet {
  el: HTMLDivElement;
  phase: number;
  anim: Animation | null;
}

interface MotionGeo {
  head: string;
  rest: string;
  w: number;
  parks: number[];
}

interface Carrier {
  lat: number;
  packets: Packet[];
  set: HTMLDivElement | null;
  geo: MotionGeo | null;
  frames: Keyframe[] | null;
  framesHide: Keyframe[] | null;
  framesOpen: Keyframe[] | null;
  parkIdx: number[];
}

interface Route extends Carrier {
  id: string;
  city: string;
  country: string;
  code: string;
  shown: number;
  hist: number[];
  tw: number;
  rect: Box | null;
  el: HTMLElement;
  msEl: HTMLElement;
  msWrap: HTMLElement;
  deltaEl: HTMLElement;
  spLine: SVGPolylineElement | null;
  spFill: SVGPolygonElement | null;
  spDot: SVGCircleElement | null;
  g: SVGGElement | null;
  pathBase: SVGPathElement | null;
  pathGlow: SVGPathElement | null;
  pathHot: SVGPathElement | null;
  pathSweep: SVGPathElement | null;
  pathKnock: SVGPathElement | null;
  portIn: SVGCircleElement | null;
  portOut: SVGCircleElement | null;
}

interface RouteGeometry {
  base: string;
  full: string;
  l: number;
  r: number;
  cy: number;
  geo: MotionGeo;
}

interface Geometry {
  mobile: boolean;
  dPc: number;
  dGame: number;
  a: { x: number; y: number };
  b: { x: number; y: number };
  ra: { x: number; y: number };
  rb: { x: number; y: number };
  routes: RouteGeometry[];
  direct: { head: string; tail: string; l: number; r: number; cy: number; geo: MotionGeo };
  trunks: [string, string];
}

interface WinnerResult {
  best: Route;
  prev: Route | null;
  changed: boolean;
}

interface ReducedQuery {
  matches: boolean;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
}

const NS = 'http://www.w3.org/2000/svg';
const ROUTE_IDS = ['r1', 'r2', 'r3', 'r4', 'r5'];
const LAT_MIN = 2;
const BAD_LO = 180;
const BAD_HI = 250;
const HIST_N = 8;
const BASE_MS = 1600;
const SP_W = 52;
const SP_H = 18;
const SP_LO = 0;
const SP_HI = 40;
const SP_K = 2.2;
const STUB_V = 14;
const MASK_PAD = 48;
const LIVE_MS = 500;
const LOOP_MS = 5200;

export function fold(text: unknown): string {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function rint(low: number, high: number): number {
  return low + Math.floor(Math.random() * (high - low + 1));
}

function num(value: unknown): number | null {
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function km(from: { lat: number; lon: number }, to: { lat: number; lon: number }): number {
  const rad = Math.PI / 180;
  const dLat = (to.lat - from.lat) * rad;
  const dLon = (to.lon - from.lon) * rad;
  const hav = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(from.lat * rad) * Math.cos(to.lat * rad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 12742 * Math.asin(Math.min(1, Math.sqrt(hav)));
}

function f(value: number): number {
  return Math.round(value * 10) / 10;
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ?? character);
}

/** Normalises any usual geolocation answer ({ country_code | countryCode | country, region…, city, lat/lon… }). */
export function normGeo(raw: unknown): Geo | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  const countryField = data.country && typeof data.country === 'object' ? (data.country as Record<string, unknown>).code : data.country;
  const country = data.country_code || data.countryCode || countryField;
  if (!country || String(country).length !== 2) return null;
  const lng = data.lng != null ? data.lng : data.longitude;
  return {
    country: String(country).toUpperCase(),
    region: String(data.region_code || data.regionCode || data.region || data.state || ''),
    city: String(data.city || ''),
    lat: num(data.lat != null ? data.lat : data.latitude),
    lon: num(data.lon != null ? data.lon : lng),
  };
}

/** `?mcgeo=BR|MG|Belo Horizonte[|lat|lon]` forces a location (QA). */
export function forcedGeo(search: string): Geo | null {
  try {
    const match = /[?&]mcgeo=([^&#]*)/.exec(search);
    if (!match) return null;
    const parts = decodeURIComponent((match[1] ?? '').replace(/\+/g, ' ')).split('|');
    return normGeo({ country: parts[0], region: parts[1], city: parts[2], lat: parts[3], lon: parts[4] });
  } catch {
    return null;
  }
}

/** Builds the visitor's five routes and five latency scenarios from the geo data (design `profileFor`). */
export function profileFor(data: GeoData, geoIn: Geo | null): Place {
  const COUNTRIES = data.countries;
  const REGIONS = data.regions || {};
  const ALIASES = data.aliases || {};
  const geo: Geo = geoIn && COUNTRIES[geoIn.country] ? geoIn : { country: 'US', region: '', city: '', lat: null, lon: null };
  const toCity = (cc: string, entry: GeoCity): City => ({ city: entry[0], code: entry[1], lat: entry[2], lon: entry[3], cc });
  const findCity = (cc: string, name: unknown): City | null => {
    const cities = COUNTRIES[cc] ? COUNTRIES[cc]![2] : [];
    let folded = fold(name);
    const alias = ALIASES[folded];
    if (alias) folded = fold(alias);
    if (!folded) return null;
    const found = cities.find((entry) => fold(entry[0]) === folded);
    return found ? toCity(cc, found) : null;
  };
  const codeOf = (name: string) => `${fold(name).replace(/ /g, '').toUpperCase()}XXX`.slice(0, 3);

  const cc = geo.country;
  const meta = COUNTRIES[cc]!;
  const hubEntry = meta[2].find((entry) => entry[0] === meta[1]) ?? meta[2][0]!;
  const hub = toCity(cc, hubEntry);
  let user: City | null = null;
  const known = findCity(cc, geo.city);
  if (known) user = known;
  else if (geo.city) {
    const at = geo.lat != null && geo.lon != null ? { lat: geo.lat, lon: geo.lon } : findCity(cc, (REGIONS[cc] || {})[String(geo.region).toUpperCase()]) || hub;
    user = { city: String(geo.city), code: codeOf(geo.city), lat: at.lat, lon: at.lon, cc };
  }
  const from = user || hub;
  const list: City[] = [];
  if (user && fold(user.city) !== fold(hub.city)) list.push(user);
  list.push(hub);
  const taken = (candidate: City) => list.some((picked) => fold(picked.city) === fold(candidate.city));
  const near = (pool: City[]) => pool.filter((candidate) => !taken(candidate)).sort((left, right) => km(from, left) - km(from, right));
  const own = near(meta[2].map((entry) => toCity(cc, entry)));
  while (list.length < 5 && own.length) list.push(own.shift()!);
  if (list.length < 5) {
    let all: City[] = [];
    Object.keys(COUNTRIES).forEach((code) => {
      if (code !== cc) COUNTRIES[code]![2].forEach((entry) => all.push(toCity(code, entry)));
    });
    all = near(all);
    while (list.length < 5 && all.length) list.push(all.shift()!);
  }
  // latency: how far each city bends the path from the visitor to the hub (fibre: ~1 ms per 100 km)
  const directKm = km(from, hub);
  const rank = list.map((candidate, index) => ({ index, detour: km(from, candidate) + km(candidate, hub) - directKm })).sort((left, right) => left.detour - right.detour || left.index - right.index);
  const base: number[] = [];
  rank.forEach((ranked, position) => {
    base[ranked.index] = position < 2 ? 0 : Math.min(40, 6 + position + Math.round(ranked.detour / 100));
  });
  const first = rank[0]!.index;
  const second = rank[1]!.index;
  const scenarios: Scenario[] = [];
  for (let step = 0; step < 5; step += 1) {
    const row: Scenario = { bad: rint(BAD_LO, BAD_HI) };
    list.forEach((_candidate, index) => {
      const id = `r${index + 1}`;
      if (index === first) row[id] = step % 2 ? rint(7, 10) : rint(2, 3);
      else if (index === second) row[id] = step % 2 ? rint(2, 4) : rint(4, 5);
      else row[id] = (base[index] ?? 0) + rint(0, 2);
    });
    scenarios.push(row);
  }
  return { key: `${cc}|${fold(user ? user.city : '')}`, cc, hub, user, routes: list, scenarios };
}

function bezier(p1x: number, p1y: number, p2x: number, p2y: number): (x: number) => number {
  const coefA = (a1: number, a2: number) => 1 - 3 * a2 + 3 * a1;
  const coefB = (a1: number, a2: number) => 3 * a2 - 6 * a1;
  const coefC = (a1: number) => 3 * a1;
  const calc = (time: number, a1: number, a2: number) => ((coefA(a1, a2) * time + coefB(a1, a2)) * time + coefC(a1)) * time;
  const slope = (time: number, a1: number, a2: number) => 3 * coefA(a1, a2) * time * time + 2 * coefB(a1, a2) * time + coefC(a1);
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let time = x;
    for (let iteration = 0; iteration < 6; iteration += 1) {
      const currentSlope = slope(time, p1x, p2x);
      if (Math.abs(currentSlope) < 1e-6) break;
      time -= (calc(time, p1x, p2x) - x) / currentSlope;
    }
    return calc(time, p1y, p2y);
  };
}
const easeOut = bezier(0.16, 1, 0.3, 1);

function reflow(element: Element): void {
  // reading a layout property restarts a CSS animation after the class is removed and re-added
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  (element as HTMLElement).offsetWidth;
}

function svgEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string> = {}): SVGElementTagNameMap[K] {
  const element = document.createElementNS(NS, tag);
  Object.entries(attrs).forEach(([name, value]) => element.setAttribute(name, value));
  return element;
}

export interface McOptions {
  /** window.location.search, for the `?mcgeo=` QA parameter */
  search: string;
  reduced: ReducedQuery;
}

export function startMulticonexao(root: HTMLElement, data: GeoData, strings: McStrings, options: McOptions): () => void {
  const q = <T extends Element = HTMLElement>(selector: string) => root.querySelector<T>(selector);
  const stage = q('[data-stage]');
  const svg = q<SVGSVGElement>('[data-wires]');
  const pkLayer = q<HTMLDivElement>('[data-packets]');
  const list = q('[data-routes]');
  const pcNode = q('[data-node="pc"]');
  const gameNode = q('[data-node="game"]');
  const directEl = q('[data-direct]');
  const directMs = q('[data-direct-ms]');
  const statusText = q('[data-status-text]');
  const liveEl = q('[data-live]');
  const bridge = q('[data-bridge]');
  const btn = q<HTMLButtonElement>('[data-retest]');
  const btnLabel = btn?.querySelector<HTMLElement>('[data-lbl]') ?? null;
  const pauseBtn = q<HTMLButtonElement>('[data-pause]');
  const clearBtn = q<HTMLButtonElement>('[data-clear]');
  const hintText = q('[data-hint]');
  const proofEl = q('[data-proof]');
  const proofSr = q('[data-proof-sr]');
  const proofGood = q('[data-proof-good]');
  const proofPct = q('[data-proof-pct]');
  const proofFill = q('[data-proof-fill]');
  const pcPlace = q('[data-pc-place]');
  const gamePlace = q('[data-game-place]');
  const resultBad = q('[data-result-bad]');
  const proofBad = q('[data-proof-bad]');
  const resultGood = q('[data-result-good]');
  const resultPct = q('[data-result-pct]');
  const descEl = q('#mc-desc');
  const wrapEl = q('.mc-routes-wrap');
  const pcIcon = pcNode?.querySelector<HTMLElement>('.mc-node-icon') ?? null;
  const gameIcon = gameNode?.querySelector<HTMLElement>('.mc-node-icon') ?? null;
  if (!stage || !svg || !pkLayer || !list || !pcNode || !gameNode || !directEl || !directMs || !statusText || !liveEl || !btn || !btnLabel || !pauseBtn || !clearBtn || !hintText || !proofEl || !wrapEl || !pcIcon || !gameIcon) return () => {};

  const { reduced } = options;
  const COUNTRIES = data.countries;
  let destroyed = false;
  const timeouts = new Set<number>();
  const intervals = new Set<number>();
  const later = (callback: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timeouts.delete(id);
      if (!destroyed) callback();
    }, ms);
    timeouts.add(id);
    return id;
  };
  const cleanups: Array<() => void> = [];
  const listen = <E extends Event>(target: EventTarget, type: string, handler: (event: E) => void) => {
    target.addEventListener(type, handler as EventListener);
    cleanups.push(() => target.removeEventListener(type, handler as EventListener));
  };

  root.classList.add('is-js');

  const ROUTES: Route[] = ROUTE_IDS.map((id) => {
    const el = list.querySelector<HTMLElement>(`[data-route="${id}"]`)!;
    return {
      id,
      city: '',
      country: '',
      code: '',
      lat: 0,
      shown: 0,
      hist: [],
      tw: 0,
      rect: null,
      el,
      msEl: el.querySelector<HTMLElement>('[data-ms]')!,
      msWrap: el.querySelector<HTMLElement>('.mc-route-ms')!,
      deltaEl: el.querySelector<HTMLElement>('[data-delta]')!,
      spLine: el.querySelector<SVGPolylineElement>('.mc-route-spark polyline'),
      spFill: el.querySelector<SVGPolygonElement>('.mc-route-spark .sp-fill'),
      spDot: el.querySelector<SVGCircleElement>('.mc-route-spark .sp-dot'),
      packets: [],
      set: null,
      geo: null,
      frames: null,
      framesHide: null,
      framesOpen: null,
      parkIdx: [],
      g: null,
      pathBase: null,
      pathGlow: null,
      pathHot: null,
      pathSweep: null,
      pathKnock: null,
      portIn: null,
      portOut: null,
    };
  });
  if (ROUTES.some((route) => !route.el || !route.msEl || !route.msWrap || !route.deltaEl)) return () => {};

  let SCENARIOS: Scenario[] = [];
  let BAD_MS = 208;
  let place: Place | null = null;
  let scenario = 0;
  let winnerId: string | null = null;
  let focusId: string | null = null;
  let pinned: string | null = null;
  let testing = false;
  let visible = true;
  let paused = false;
  let boost = 1;
  let running = false;
  const supportsMotion = typeof pkLayer.animate === 'function';
  const direct: Carrier = { lat: BAD_MS, packets: [], set: null, geo: null, frames: null, framesHide: null, framesOpen: null, parkIdx: [] };

  const isMobile = () => getComputedStyle(stage).getPropertyValue('--mc-mobile').trim() === '1';
  const byId = (id: string | null) => ROUTES.find((route) => route.id === id) ?? null;

  function setProfile(profile: Place) {
    place = profile;
    ROUTES.forEach((route, index) => {
      const target = profile.routes[index]!;
      Object.assign(route, { city: target.city, country: COUNTRIES[target.cc]?.[0] ?? target.cc, code: target.code });
    });
    SCENARIOS = profile.scenarios;
    BAD_MS = SCENARIOS[0]!.bad ?? 208;
  }
  setProfile(profileFor(data, forcedGeo(options.search)));

  /* ---- sparklines */
  const spY = (lat: number) => SP_H - 1.5 - (SP_H - 3) * Math.max(0, Math.min(1, (lat - SP_LO) / (SP_HI - SP_LO)));
  function drawSpark(route: Route) {
    const count = route.hist.length;
    if (!count || !route.spLine || !route.spFill || !route.spDot) return;
    const mean = route.hist.reduce((sum, value) => sum + value, 0) / count;
    const base = spY(mean);
    const points = route.hist.map((value, index) => `${f(1.5 + ((SP_W - 3) * index) / (HIST_N - 1))},${f(Math.max(1.5, Math.min(SP_H - 1.5, base - (value - mean) * SP_K)))}`);
    route.spLine.setAttribute('points', points.join(' '));
    route.spFill.setAttribute('points', `1.5,${SP_H} ${points.join(' ')} ${f(1.5 + ((SP_W - 3) * (count - 1)) / (HIST_N - 1))},${SP_H}`);
    const last = (points[count - 1] ?? '0,0').split(',');
    route.spDot.setAttribute('cx', last[0] ?? '0');
    route.spDot.setAttribute('cy', last[1] ?? '0');
  }
  function pushReading(route: Route) {
    route.hist.push(route.lat);
    if (route.hist.length > HIST_N) route.hist.shift();
    drawSpark(route);
  }
  function seedHist(route: Route, amp: number) {
    const floor = route.lat <= LAT_MIN;
    const hist: number[] = [];
    for (let index = 0; index < HIST_N - 1; index += 1) hist.push(Math.max(LAT_MIN, route.lat + (floor ? 1 + Math.round(Math.random() * amp) : Math.round((Math.random() * 2 - 1) * amp))));
    hist.push(route.lat);
    Object.assign(route, { hist });
  }

  function setShown(route: Route, value: number) {
    Object.assign(route, { shown: value });
    route.msEl.textContent = String(value);
  }
  function paintRow(route: Route) {
    const cityEl = route.el.querySelector('.mc-route-city');
    const countryEl = route.el.querySelector('.mc-route-country');
    const codeEl = route.el.querySelector('.mc-route-cc');
    if (cityEl) cityEl.textContent = route.city;
    if (countryEl) countryEl.textContent = route.country;
    if (codeEl) codeEl.textContent = route.code;
  }
  function paintPlace() {
    if (!place) return;
    if (pcPlace) pcPlace.textContent = place.user ? place.user.city : strings.pcPlaceDefault;
    if (gamePlace) gamePlace.textContent = strings.gamePlace(place.hub.city);
    if (descEl)
      descEl.textContent = strings.desc(
        ROUTES.map((route) => route.city),
        place.hub.city
      );
  }
  function setBad(value: number, amp: number) {
    BAD_MS = Math.max(BAD_LO, Math.min(BAD_HI, value + (amp ? rint(-6, 6) : 0)));
    direct.lat = BAD_MS;
    if (proofBad) proofBad.textContent = String(BAD_MS);
    if (resultBad) resultBad.textContent = String(BAD_MS);
  }

  ROUTES.forEach((route) => {
    Object.assign(route, { lat: SCENARIOS[0]![route.id] ?? 0 });
    route.shown = route.lat;
    seedHist(route, 1);
    drawSpark(route);
  });

  function publishPlace() {
    try {
      // the host-page contract from the design: window.__npMcPlace + np:mc-place
      writeMcPlace(place as unknown as McPlace);
      window.dispatchEvent(new CustomEvent(MC_PLACE_EVENT, { detail: place }));
    } catch {
      /* a host without CustomEvent simply gets no mirror */
    }
  }

  function relocate(geo: Geo) {
    const profile = profileFor(data, geo);
    if (place && profile.key === place.key) return;
    setProfile(profile);
    publishPlace();
    scenario = 0;
    ROUTES.forEach((route) => {
      Object.assign(route, { lat: SCENARIOS[0]![route.id] ?? 0 });
      setShown(route, route.lat);
      paintRow(route);
      seedHist(route, 1);
      drawSpark(route);
    });
    paintPlace();
    setBad(BAD_MS, 0);
    const res = applyWinner(false);
    if (running) setRates();
    if (built) reaim(res);
    announce(res, '');
  }
  function askGeo() {
    if (forcedGeo(options.search) || !GEO_ENDPOINT || typeof fetch !== 'function') return;
    sharedGeo().then((raw) => {
      if (destroyed) return;
      const geo = normGeo(raw);
      if (geo) relocate(geo);
    });
  }

  ROUTES.forEach((route) => {
    setShown(route, route.lat);
    paintRow(route);
  });
  paintPlace();
  setBad(BAD_MS, 0);
  askGeo();
  publishPlace();

  /* ---- stage (wires + packets), built lazily near the viewport */
  let built = false;
  const ports: Record<'pc' | 'game', SVGCircleElement | null> = { pc: null, game: null };
  let measure: SVGPathElement | null = null;
  let mask: SVGMaskElement | null = null;
  let maskBg: SVGRectElement | null = null;
  let directPath: SVGPathElement | null = null;
  let directTail: SVGPathElement | null = null;
  const dports: SVGCircleElement[] = [];
  const trunks: SVGPathElement[] = [];

  function makePackets(count: number, cls: string) {
    const set = document.createElement('div');
    set.className = `pk-set${cls ? ` ${cls}` : ''}`;
    const packets: Packet[] = [];
    for (let index = 0; index < count; index += 1) {
      const el = document.createElement('div');
      el.className = 'pk';
      set.appendChild(el);
      packets.push({ el, phase: (index + 0.15) / count, anim: null });
    }
    pkLayer!.appendChild(set);
    return { set, packets };
  }

  function buildStage() {
    if (built || destroyed) return;
    built = true;
    const portLayer = svgEl('g');
    (['pc', 'game'] as const).forEach((key) => {
      const circle = svgEl('circle', { r: '4', class: `w-port${winnerId ? ' is-hot' : ''}` });
      portLayer.appendChild(circle);
      ports[key] = circle;
    });
    measure = svgEl('path', { fill: 'none', stroke: 'none' });
    svg!.appendChild(measure);
    const defs = svgEl('defs');
    mask = svgEl('mask', { id: 'mc-knock', maskUnits: 'userSpaceOnUse', x: '0', y: '0', width: '100%', height: '100%' });
    maskBg = svgEl('rect', { width: '100%', height: '100%', fill: '#fff' });
    mask.appendChild(maskBg);
    defs.appendChild(mask);
    svg!.appendChild(defs);
    const directG = svgEl('g', { class: 'w-directwrap' });
    const directInner = svgEl('g', { mask: 'url(#mc-knock)' });
    directPath = svgEl('path', { class: 'w-direct' });
    directTail = svgEl('path', { class: 'w-direct' });
    directInner.appendChild(directPath);
    directInner.appendChild(directTail);
    directG.appendChild(directInner);
    for (let index = 0; index < 4; index += 1) {
      const circle = svgEl('circle', { r: index < 2 ? '2.5' : '4', class: `w-dport${index < 2 ? '' : ' w-dport--end'}` });
      directG.appendChild(circle);
      dports.push(circle);
    }
    svg!.appendChild(directG);
    const directPackets = makePackets(2, 'is-direct');
    direct.set = directPackets.set;
    direct.packets = directPackets.packets;
    const trunkG = svgEl('g', { class: 'w-trunks' });
    for (let index = 0; index < 2; index += 1) {
      const trunk = svgEl('path', { class: 'w-base w-trunk' });
      trunkG.appendChild(trunk);
      trunks.push(trunk);
    }
    svg!.appendChild(trunkG);
    ROUTES.forEach((route) => {
      const group = svgEl('g', { 'data-wire': route.id });
      const base = svgEl('path', { class: 'w-base' });
      const glow = svgEl('path', { class: 'w-glow' });
      const hotwrap = svgEl('g', { class: 'w-hotwrap' });
      const hot = svgEl('path', { class: 'w-hot' });
      const sweep = svgEl('path', { class: 'w-sweep' });
      hotwrap.appendChild(hot);
      const portIn = svgEl('circle', { r: '2.75', class: 'w-rport' });
      const portOut = svgEl('circle', { r: '2.75', class: 'w-rport' });
      [base, glow, hotwrap, sweep, portIn, portOut].forEach((node) => group.appendChild(node));
      svg!.appendChild(group);
      const knock = svgEl('path', { class: 'w-knock' });
      mask!.appendChild(knock);
      const routePackets = makePackets(3, '');
      Object.assign(route, { g: group, pathBase: base, pathGlow: glow, pathHot: hot, pathSweep: sweep, portIn, portOut, pathKnock: knock, set: routePackets.set, packets: routePackets.packets });
      if (route.id === winnerId) {
        group.classList.add('is-winner');
        routePackets.set.classList.add('is-winner');
      }
      if (route.id === focusId) {
        group.classList.add('is-focus');
        routePackets.set.classList.add('is-focus');
      }
    });
    svg!.appendChild(portLayer);
    layout(true);
  }

  const box = (rect: DOMRect, origin: DOMRect): Box => ({ l: rect.left - origin.left, t: rect.top - origin.top, r: rect.right - origin.left, b: rect.bottom - origin.top, w: rect.width, h: rect.height, cx: rect.left - origin.left + rect.width / 2, cy: rect.top - origin.top + rect.height / 2 });
  const rel = (element: Element, origin: DOMRect) => box(element.getBoundingClientRect(), origin);
  const fanA = (x1: number, y1: number, x2: number, y2: number) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return `C${f(x1 + dx * 0.42)},${f(y1 + dy * 0.34)} ${f(x2 - dx * 0.42)},${f(y2)} ${f(x2)},${f(y2)}`;
  };
  const fanB = (x1: number, y1: number, x2: number, y2: number) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    return `C${f(x1 + dx * 0.42)},${f(y1)} ${f(x2 - dx * 0.42)},${f(y2 - dy * 0.34)} ${f(x2)},${f(y2)}`;
  };
  const stubIn = (tx: number, cy: number, cl: number) => {
    const run = Math.max(6, cl - tx);
    return `C${f(tx)},${f(cy - STUB_V * 0.45)} ${f(tx + run * 0.5)},${f(cy)} ${f(cl)},${f(cy)}`;
  };
  const stubOut = (cr: number, cy: number, rx: number) => {
    const run = Math.max(6, rx - cr);
    return `C${f(cr + run * 0.5)},${f(cy)} ${f(rx)},${f(cy + STUB_V * 0.55)} ${f(rx)},${f(cy + STUB_V)}`;
  };

  function sampleFrames(pathData: string, hideA: number, hideB: number, parks: number[]) {
    if (!measure || typeof measure.getTotalLength !== 'function') return null;
    measure.setAttribute('d', pathData);
    const len = measure.getTotalLength();
    if (!len) return null;
    const steps = Math.max(40, Math.min(160, Math.round(len / 7)));
    const points: DOMPoint[] = [];
    for (let index = 0; index <= steps; index += 1) points.push(measure.getPointAtLength((len * index) / steps));
    const times = [0];
    for (let index = 1; index <= steps; index += 1) {
      const at = (len * (index - 0.5)) / steps;
      times.push((times[index - 1] ?? 0) + (at > hideA && at < hideB ? 0.25 : 1));
    }
    const total = times[steps] ?? 1;
    const frames: Keyframe[] = [];
    let prevAngle = 0;
    for (let index = 0; index <= steps; index += 1) {
      const before = points[Math.max(0, index - 1)]!;
      const after = points[Math.min(steps, index + 1)]!;
      let angle = (Math.atan2(after.y - before.y, after.x - before.x) * 180) / Math.PI;
      if (index) {
        while (angle - prevAngle > 180) angle -= 360;
        while (angle - prevAngle < -180) angle += 360;
      }
      prevAngle = angle;
      const at = (len * index) / steps;
      const point = points[index]!;
      frames.push({ offset: (times[index] ?? 0) / total, transform: `translate(${f(point.x)}px,${f(point.y)}px) rotate(${f(angle)}deg)`, opacity: at > hideA + 3 && at < hideB - 3 ? 0 : 1 });
    }
    const parkIdx = parks.map((park) => Math.max(0, Math.min(steps, Math.round((park / len) * steps))));
    return { frames, parkIdx };
  }

  const framesFor = (carrier: Carrier) => (carrier.set && carrier.set.classList.contains('is-winner') ? carrier.framesOpen : carrier.framesHide);
  function makeAnim(packet: Packet, frames: Keyframe[]) {
    Object.assign(packet, { anim: packet.el.animate(frames, { duration: BASE_MS, iterations: Infinity, easing: 'linear', delay: -packet.phase * BASE_MS }) });
  }
  function setPath(carrier: Carrier) {
    const { geo } = carrier;
    if (!geo || !measure || typeof measure.getTotalLength !== 'function') return;
    measure.setAttribute('d', geo.head);
    const lenA = measure.getTotalLength();
    const pathData = `${geo.head} ${geo.rest}`;
    measure.setAttribute('d', pathData);
    const len = measure.getTotalLength();
    const lenB = Math.max(0, len - lenA - geo.w);
    if (!len) return;
    const parks = geo.parks.map((park) => (park >= 0 ? park * lenA : lenA + geo.w + -park * lenB));
    const res = sampleFrames(pathData, lenA, lenA + geo.w, parks);
    if (!res) return;
    Object.assign(carrier, {
      framesHide: res.frames,
      framesOpen: res.frames.map((frame) => ({ offset: frame.offset, transform: frame.transform })),
      parkIdx: res.parkIdx,
    });
    carrier.frames = framesFor(carrier);
    const frames = carrier.frames ?? res.frames;
    carrier.packets.forEach((packet, index) => {
      if (packet.anim) {
        try {
          (packet.anim.effect as KeyframeEffect).setKeyframes(frames);
        } catch {
          const time = packet.anim.currentTime;
          packet.anim.cancel();
          Object.assign(packet, { anim: null });
          makeAnim(packet, frames);
          if (packet.anim && time != null) (packet.anim as Animation).currentTime = time;
        }
      } else if (!running) {
        const frame = frames[res.parkIdx[index % res.parkIdx.length] ?? 0];
        if (frame) packet.el.style.setProperty('--pk-t', String(frame.transform));
      }
    });
  }
  function refreshFrames(carrier: Carrier) {
    if (!carrier.framesHide) return;
    const want = framesFor(carrier);
    if (carrier.frames === want || !want) return;
    Object.assign(carrier, { frames: want });
    carrier.packets.forEach((packet) => {
      if (!packet.anim) return;
      try {
        (packet.anim.effect as KeyframeEffect).setKeyframes(want);
      } catch {
        /* an effect that refuses new keyframes keeps its old path until the next layout */
      }
    });
  }

  let lastKey = '';
  const geoState: { mobile: boolean; pc: Box | null; game: Box | null; dir: Box | null; list: Box | null; rPc: number; rGame: number } = { mobile: false, pc: null, game: null, dir: null, list: null, rPc: 0, rGame: 0 };
  const axis: { y: number | null; raf: number; done: (() => void) | null } = { y: null, raf: 0, done: null };
  const endDy = { pc: 0, game: 0 };
  const snap = { x: 0, y: 0 };
  const shiftXY = (rc: Box, dx: number, dy: number): Box => ({ l: rc.l + dx, r: rc.r + dx, t: rc.t + dy, b: rc.b + dy, w: rc.w, h: rc.h, cx: rc.cx + dx, cy: rc.cy + dy });
  const shiftBox = (rc: Box, dy: number): Box => ({ l: rc.l, r: rc.r, t: rc.t + dy, b: rc.b + dy, w: rc.w, h: rc.h, cx: rc.cx, cy: rc.cy + dy });
  const sideOff = (rc: Box, radius: number) => Math.max(10, Math.min(22, rc.h / 2 - radius - 8));

  function layout(force: boolean) {
    if (!built || destroyed || !mask || !maskBg) return;
    const origin = svg!.getBoundingClientRect();
    if (!origin.width || !origin.height) return;
    const dr = directEl!.getBoundingClientRect();
    const wr = wrapEl!.getBoundingClientRect();
    let rects = ROUTES.map((route) => rel(route.el, origin));
    const mobileNow = isMobile();
    const dpr = window.devicePixelRatio || 1;
    const ux = wr.left - snap.x;
    const uy = wr.top - snap.y;
    const nx = Math.round(ux * dpr) / dpr - ux;
    const ny = Math.round(uy * dpr) / dpr - uy;
    const ddx = nx - snap.x;
    const ddy = ny - snap.y;
    const resnap = Math.abs(ddx) > 0.005 || Math.abs(ddy) > 0.005;
    const firstRect = rects[0]!;
    const lastRect = rects[rects.length - 1]!;
    const key = [Math.round(origin.width), Math.round(origin.height), mobileNow ? 1 : 0, Math.round(firstRect.t - snap.y), Math.round(lastRect.b - snap.y), Math.round(rects[1]!.t - snap.y), Math.round(dr.top - snap.y - origin.top)].join('|');
    if (!force && !resnap && key === lastKey) return;
    lastKey = key;
    let pcR = rel(pcIcon!, origin);
    let gmR = rel(gameIcon!, origin);
    const rPc = parseFloat(getComputedStyle(pcIcon!).borderTopLeftRadius) || 0;
    const rGame = parseFloat(getComputedStyle(gameIcon!).borderTopLeftRadius) || 0;
    if (!mobileNow) {
      pcR = shiftBox(pcR, -endDy.pc);
      gmR = shiftBox(gmR, -endDy.game);
    }
    if (!pcR.w || !gmR.w || rects.some((rc) => !rc.w || !rc.h)) return;
    let dirB = box(dr, origin);
    let listB: Box = { l: firstRect.l, t: firstRect.t, r: firstRect.r, b: lastRect.b, w: firstRect.w, h: lastRect.b - firstRect.t, cx: firstRect.cx, cy: (firstRect.t + lastRect.b) / 2 };
    if (resnap) {
      rects = rects.map((rc) => shiftXY(rc, ddx, ddy));
      dirB = shiftXY(dirB, ddx, ddy);
      listB = shiftXY(listB, ddx, ddy);
    }
    ROUTES.forEach((route, index) => Object.assign(route, { rect: rects[index] ?? null }));
    if (resnap) {
      snap.x = nx;
      snap.y = ny;
      if (Math.abs(nx) < 0.005 && Math.abs(ny) < 0.005) wrapEl!.style.removeProperty('--mc-snap');
      else wrapEl!.style.setProperty('--mc-snap', `translate(${Math.round(nx * 1000) / 1000}px,${Math.round(ny * 1000) / 1000}px)`);
    }
    Object.assign(geoState, { mobile: mobileNow, pc: pcR, game: gmR, dir: dirB, list: listB, rPc, rGame });
    svg!.setAttribute('viewBox', `0 0 ${origin.width} ${origin.height}`);
    [mask, maskBg].forEach((node) => {
      node.setAttribute('x', String(-MASK_PAD));
      node.setAttribute('y', String(-MASK_PAD));
      node.setAttribute('width', String(origin.width + 2 * MASK_PAD));
      node.setAttribute('height', String(origin.height + 2 * MASK_PAD));
    });
    const mid = !!axis.raf;
    if (mid) {
      cancelAnimationFrame(axis.raf);
      axis.raf = 0;
    }
    axis.y = geoState.mobile ? null : targetAxisY();
    drawWires();
    stage!.classList.add('is-ready');
    if (motionArmed) scheduleMotion();
    if (mid) settleGlide();
  }
  function targetAxisY(): number {
    const winner = winnerId ? byId(winnerId) : null;
    return winner && winner.rect ? winner.rect.cy : (geoState.list?.cy ?? 0);
  }
  function computeGeo(axisY: number | null): Geometry | null {
    if (!geoState.pc || !geoState.game || !geoState.dir) return null;
    const { mobile } = geoState;
    const { dir } = geoState;
    let { pc } = geoState;
    let { game } = geoState;
    let dPc = 0;
    let dGame = 0;
    if (!mobile && axisY != null) {
      dPc = axisY - pc.cy;
      dGame = axisY - game.cy;
      pc = shiftBox(pc, dPc);
      game = shiftBox(game, dGame);
    }
    const a = mobile ? { x: pc.cx, y: pc.b + 1 } : { x: pc.r + 1, y: pc.cy };
    const b = mobile ? { x: game.cx, y: game.t - 1 } : { x: game.l - 1, y: game.cy };
    const ra = mobile ? { x: pc.l - 1, y: pc.b - (geoState.rPc + 5) } : { x: pc.r + 1, y: pc.cy + sideOff(pc, geoState.rPc) };
    const rb = mobile ? { x: game.r + 1, y: game.t + (geoState.rGame + 5) } : { x: game.l - 1, y: game.cy + sideOff(game, geoState.rGame) };
    let trunkPaths: [string, string] = ['', ''];
    if (mobile) {
      const firstCy = ROUTES[0]!.rect?.cy ?? 0;
      const lastCy = ROUTES[ROUTES.length - 1]!.rect?.cy ?? 0;
      trunkPaths = [`M${f(a.x)},${f(a.y)} L${f(a.x)},${f(lastCy - STUB_V)}`, `M${f(b.x)},${f(firstCy + STUB_V)} L${f(b.x)},${f(b.y)}`];
    }
    const routes = ROUTES.map((route): RouteGeometry => {
      const rc = route.rect!;
      let segB: string;
      let dHead: string;
      let base: string | null;
      if (mobile) {
        const sIn = stubIn(a.x, rc.cy, rc.l);
        const sOut = stubOut(rc.r, rc.cy, b.x);
        const segA = `L${f(a.x)},${f(rc.cy - STUB_V)} ${sIn}`;
        segB = `${sOut} L${f(b.x)},${f(b.y)}`;
        dHead = `M${f(a.x)},${f(a.y)} ${segA}`;
        base = `M${f(a.x)},${f(rc.cy - STUB_V)} ${sIn} M${f(rc.r)},${f(rc.cy)} ${sOut}`;
      } else {
        const segA = fanA(a.x, a.y, rc.l, rc.cy);
        segB = fanB(rc.r, rc.cy, b.x, b.y);
        dHead = `M${f(a.x)},${f(a.y)} ${segA}`;
        base = null;
      }
      const full = `${dHead} M${f(rc.r)},${f(rc.cy)} ${segB}`;
      return { base: base || full, full, l: rc.l, r: rc.r, cy: rc.cy, geo: { head: dHead, rest: `L${f(rc.r)},${f(rc.cy)} ${segB}`, w: rc.w, parks: [0.45, 0.85, -0.6] } };
    });
    const dA = mobile ? `L${f(ra.x)},${f(dir.cy - STUB_V)} ${stubIn(ra.x, dir.cy, dir.l)}` : fanA(ra.x, ra.y, dir.l, dir.cy);
    const dB = mobile ? `${stubOut(dir.r, dir.cy, rb.x)} L${f(rb.x)},${f(rb.y)}` : fanB(dir.r, dir.cy, rb.x, rb.y);
    const dirHead = `M${f(ra.x)},${f(ra.y)} ${dA}`;
    return {
      mobile,
      dPc,
      dGame,
      a,
      b,
      ra,
      rb,
      routes,
      trunks: trunkPaths,
      direct: { head: dirHead, tail: `M${f(dir.r)},${f(dir.cy)} ${dB}`, l: dir.l, r: dir.r, cy: dir.cy, geo: { head: dirHead, rest: `L${f(dir.r)},${f(dir.cy)} ${dB}`, w: dir.w, parks: [0.55, -0.5] } },
    };
  }
  function fitDash(path: SVGPathElement, pathData: string) {
    path.setAttribute('d', pathData);
    if (typeof path.getTotalLength !== 'function') return;
    const len = path.getTotalLength();
    const DASH = 6;
    if (!len) return;
    const count = Math.max(1, Math.round((len - DASH) / 13));
    path.setAttribute('stroke-dasharray', `${DASH} ${f(Math.max(3, (len - DASH) / count - DASH))}`);
  }
  function paintGeo(geometry: Geometry) {
    if (!geometry.mobile) {
      pcNode!.style.setProperty('--mc-dy', `${f(geometry.dPc)}px`);
      gameNode!.style.setProperty('--mc-dy', `${f(geometry.dGame)}px`);
      endDy.pc = geometry.dPc;
      endDy.game = geometry.dGame;
    }
    ports.pc?.setAttribute('cx', String(f(geometry.a.x)));
    ports.pc?.setAttribute('cy', String(f(geometry.a.y)));
    ports.game?.setAttribute('cx', String(f(geometry.b.x)));
    ports.game?.setAttribute('cy', String(f(geometry.b.y)));
    trunks[0]?.setAttribute('d', geometry.trunks[0]);
    trunks[1]?.setAttribute('d', geometry.trunks[1]);
    ROUTES.forEach((route, index) => {
      const routeGeometry = geometry.routes[index]!;
      route.pathBase?.setAttribute('d', routeGeometry.base);
      [route.pathGlow, route.pathHot, route.pathSweep, route.pathKnock].forEach((path) => path?.setAttribute('d', routeGeometry.full));
      route.portIn?.setAttribute('cx', String(f(routeGeometry.l)));
      route.portIn?.setAttribute('cy', String(f(routeGeometry.cy)));
      route.portOut?.setAttribute('cx', String(f(routeGeometry.r)));
      route.portOut?.setAttribute('cy', String(f(routeGeometry.cy)));
    });
    const lane = geometry.direct;
    if (directPath) fitDash(directPath, lane.head);
    if (directTail) fitDash(directTail, lane.tail);
    const dportPositions = [
      [lane.l, lane.cy],
      [lane.r, lane.cy],
      [geometry.ra.x, geometry.ra.y],
      [geometry.rb.x, geometry.rb.y],
    ];
    dports.forEach((dport, index) => {
      const [x, y] = dportPositions[index] ?? [0, 0];
      dport.setAttribute('cx', String(f(x ?? 0)));
      dport.setAttribute('cy', String(f(y ?? 0)));
    });
  }
  function setMotionGeo(geometry: Geometry | null) {
    if (!geometry) return;
    ROUTES.forEach((route, index) => Object.assign(route, { geo: geometry.routes[index]?.geo ?? null }));
    direct.geo = geometry.direct.geo;
  }
  function drawWires() {
    const geometry = computeGeo(axis.y);
    if (!geometry) return;
    paintGeo(geometry);
    setMotionGeo(geometry);
  }
  const springOut = (progress: number) => {
    const overshoot = 0.9;
    const back = progress - 1;
    return 1 + (overshoot + 1) * back * back * back + overshoot * back * back;
  };
  function settleGlide() {
    if (axis.raf) {
      cancelAnimationFrame(axis.raf);
      axis.raf = 0;
    }
    const { done } = axis;
    axis.done = null;
    if (done) done();
  }
  function glideAxis(duration: number, done?: () => void) {
    settleGlide();
    if (!geoState.pc || geoState.mobile) {
      if (done) done();
      return;
    }
    const to = targetAxisY();
    const from = axis.y;
    if (from == null || reduced.matches || !duration || Math.abs(to - from) < 0.5) {
      axis.y = to;
      drawWires();
      if (done) done();
      return;
    }
    const startTime = performance.now();
    axis.done = done ?? null;
    const step = (now: number) => {
      if (destroyed) return;
      const progress = Math.min(1, (now - startTime) / duration);
      axis.y = from + (to - from) * springOut(progress);
      drawWires();
      if (progress < 1) {
        axis.raf = requestAnimationFrame(step);
        return;
      }
      axis.raf = 0;
      axis.y = to;
      drawWires();
      settleGlide();
    };
    axis.raf = requestAnimationFrame(step);
  }
  function burst(route: Route) {
    if (reduced.matches) return;
    route.el.classList.remove('is-burst');
    reflow(route.el);
    route.el.classList.add('is-burst');
  }
  function reaim(res: WinnerResult, callback?: () => void) {
    const moves = !geoState.mobile && res.changed && !!geoState.pc;
    const ignite = () => {
      stage!.classList.remove('is-aiming');
      burst(res.best);
      if (callback) callback();
    };
    if (!moves) {
      ignite();
      return;
    }
    stage!.classList.add('is-aiming');
    const go = () => {
      if (motionArmed) {
        setMotionGeo(computeGeo(targetAxisY()));
        scheduleMotion(() => glideAxis(720, ignite));
      } else glideAxis(720, ignite);
    };
    if (reduced.matches) go();
    else later(go, 180);
  }

  let motionArmed = false;
  let motionQueue: Carrier[] = [];
  let motionRaf = 0;
  let motionCbs: Array<() => void> = [];
  function scheduleMotion(callback?: () => void) {
    if (!built) {
      if (callback) callback();
      return;
    }
    motionQueue = [...ROUTES, direct];
    if (callback) motionCbs.push(callback);
    cancelAnimationFrame(motionRaf);
    motionRaf = requestAnimationFrame(motionStep);
  }
  function motionStep() {
    if (destroyed) return;
    const next = motionQueue.shift();
    if (next) setPath(next);
    if (motionQueue.length) {
      motionRaf = requestAnimationFrame(motionStep);
      return;
    }
    motionRaf = 0;
    stage!.classList.add('is-motion');
    if (reduced.matches || !supportsMotion) drawStatic();
    else syncRun();
    const callbacks = motionCbs;
    motionCbs = [];
    callbacks.forEach((callback) => callback());
  }
  function armMotion() {
    if (motionArmed) return;
    buildStage();
    motionArmed = true;
    if (stage!.classList.contains('is-ready')) scheduleMotion();
  }
  const durFor = (lat: number) => 700 + lat * 14;
  const rateFor = (lat: number) => (BASE_MS / durFor(lat)) * boost;
  function ensureAnims(carrier: Carrier) {
    const { frames } = carrier;
    if (!frames) return;
    carrier.packets.forEach((packet) => {
      if (packet.anim) return;
      packet.el.style.removeProperty('--pk-t');
      makeAnim(packet, frames);
    });
  }
  function setRates() {
    ROUTES.forEach((route) => {
      route.packets.forEach((packet) => {
        if (packet.anim) packet.anim.playbackRate = rateFor(route.lat);
      });
    });
    direct.packets.forEach((packet) => {
      if (packet.anim) packet.anim.playbackRate = rateFor(direct.lat) * 0.8;
    });
  }
  function eachAnim(callback: (animation: Animation) => void) {
    [...ROUTES, direct].forEach((carrier) =>
      carrier.packets.forEach((packet) => {
        if (packet.anim) callback(packet.anim);
      })
    );
  }
  function start() {
    if (reduced.matches || paused || !supportsMotion || !built) return;
    running = true;
    root.classList.add('is-live');
    ROUTES.forEach(ensureAnims);
    ensureAnims(direct);
    setRates();
    eachAnim((animation) => {
      if (animation.playState !== 'running') animation.play();
    });
  }
  function stop() {
    running = false;
    root.classList.remove('is-live');
    eachAnim((animation) => animation.pause());
  }
  function drawStatic() {
    running = false;
    root.classList.remove('is-live');
    const park = (carrier: Carrier) => {
      const { frames } = carrier;
      if (!frames) return;
      carrier.packets.forEach((packet, index) => {
        if (packet.anim) {
          packet.anim.cancel();
          Object.assign(packet, { anim: null });
        }
        const frame = frames[carrier.parkIdx[index % carrier.parkIdx.length] ?? 0];
        if (frame) packet.el.style.setProperty('--pk-t', String(frame.transform));
      });
    };
    ROUTES.forEach(park);
    park(direct);
  }

  /* ---- figures, labels and status */
  const pctFor = (lat: number) => Math.max(0, Math.min(99, Math.round(((BAD_MS - lat) / BAD_MS) * 100)));
  const proof = { lat: -1, bad: -1 };
  function paintProof(lat: number, pct: number) {
    if (proofGood) proofGood.textContent = String(lat);
    if (proofPct) proofPct.textContent = String(pct);
    if (resultGood) resultGood.textContent = String(lat);
    if (resultPct) resultPct.textContent = String(pct);
  }
  function paintBar(lat: number) {
    if (proofFill) (proofFill as HTMLElement).style.setProperty('--mc-fill', `${Math.round((lat / BAD_MS) * 1000) / 10}%`);
  }
  function setProof(lat: number) {
    if (proof.lat === lat && proof.bad === BAD_MS) return;
    proof.lat = lat;
    proof.bad = BAD_MS;
    if (proofSr) proofSr.textContent = strings.proofSr(BAD_MS, lat, pctFor(lat));
    paintBar(lat);
  }
  const stMs = (route: Route) => `<span data-st-ms="${route.id}">${route.shown}</span>`;
  const stDelta = (route: Route, best: Route) => `<span data-st-delta="${route.id}">${Math.max(0, route.shown - best.shown)}</span>`;
  const cityHtml = (route: Route) => escapeHtml(route.city);

  function syncDerived() {
    const winner = winnerId ? byId(winnerId) : null;
    if (!winner) return;
    const winnerShown = winner.shown;
    ROUTES.forEach((route) => {
      route.deltaEl.textContent = route === winner ? strings.deltaActive : strings.delta(Math.max(0, route.shown - winnerShown));
    });
    directMs!.textContent = String(BAD_MS - winnerShown);
    paintProof(winnerShown, pctFor(winnerShown));
    statusText!.querySelectorAll('[data-st-ms],[data-st-delta]').forEach((element) => {
      const msId = element.getAttribute('data-st-ms');
      if (msId) {
        const route = byId(msId);
        if (route) Object.assign(element, { textContent: String(route.shown) });
      } else {
        const route = byId(element.getAttribute('data-st-delta'));
        if (route) Object.assign(element, { textContent: String(Math.max(0, route.shown - winnerShown)) });
      }
    });
  }
  function rankOf(route: Route) {
    const position = ROUTES.indexOf(route);
    return 1 + ROUTES.filter((other, index) => other !== route && (other.lat < route.lat || (other.lat === route.lat && index < position))).length;
  }
  function flip(element: Element | null) {
    if (!element || reduced.matches) return;
    element.classList.remove('is-flip');
    reflow(element);
    element.classList.add('is-flip');
  }
  function tickMs(route: Route, from: number, to: number) {
    if (route.tw) {
      cancelAnimationFrame(route.tw);
      Object.assign(route, { tw: 0 });
    }
    const badge = directMs!.textContent;
    const land = () => {
      setShown(route, to);
      syncDerived();
      flip(route.msWrap);
      if (built && directMs!.textContent !== badge) flip(directMs!.parentElement);
    };
    if (reduced.matches || Math.abs(to - from) < 3) {
      land();
      return;
    }
    const startTime = performance.now();
    const duration = 560;
    const step = (now: number) => {
      if (destroyed) return;
      const progress = Math.min(1, (now - startTime) / duration);
      if (progress < 1) {
        setShown(route, Math.round(from + (to - from) * easeOut(progress)));
        syncDerived();
        Object.assign(route, { tw: requestAnimationFrame(step) });
        return;
      }
      Object.assign(route, { tw: 0 });
      land();
    };
    Object.assign(route, { tw: requestAnimationFrame(step) });
  }
  const labelFor = (route: Route, best: Route) => (route.id === best.id ? strings.routeLabelWinner(route.city, route.country, route.lat) : strings.routeLabel(route.city, route.country, route.lat, route.lat - best.lat));

  function applyWinner(withBurst: boolean): WinnerResult {
    const best = [...ROUTES].sort((left, right) => left.lat - right.lat)[0]!;
    const prev = winnerId ? byId(winnerId) : null;
    const changed = prev !== null && best.id !== prev.id;
    winnerId = best.id;
    ROUTES.forEach((route) => {
      const win = route.id === winnerId;
      route.el.classList.toggle('is-winner', win);
      if (route.g && route.set) {
        route.g.classList.toggle('is-winner', win);
        route.set.classList.toggle('is-winner', win);
        refreshFrames(route);
      }
      route.el.classList.remove('is-burst');
      route.el.setAttribute('aria-label', labelFor(route, best));
    });
    if (built) {
      ports.pc?.classList.add('is-hot');
      ports.game?.classList.add('is-hot');
    }
    if (withBurst) burst(best);
    directEl!.setAttribute('aria-label', strings.directLabel(BAD_MS - best.lat));
    const badge = directMs!.textContent;
    syncDerived();
    if (built && directMs!.textContent !== badge) flip(directMs!.parentElement);
    setProof(best.lat);
    return { best, prev, changed };
  }
  const jitter = (value: number, amp: number) => Math.max(LAT_MIN, value + Math.round((Math.random() * 2 - 1) * Math.min(1, amp)));
  function applyScenario(sc: Scenario, amp: number, withBurst: boolean) {
    setBad(sc.bad ?? BAD_MS, amp);
    ROUTES.forEach((route) => {
      Object.assign(route, { lat: jitter(sc[route.id] ?? route.lat, amp) });
      pushReading(route);
    });
    const res = applyWinner(false);
    ROUTES.forEach((route) => {
      if (route.shown !== route.lat) tickMs(route, route.shown, route.lat);
    });
    if (running) setRates();
    if (res.changed) reaim(res);
    else if (withBurst) burst(res.best);
    return res;
  }
  let scrambleT = 0;
  function uncrown() {
    ROUTES.forEach((route) => {
      route.el.classList.remove('is-winner', 'is-burst');
      if (route.g && route.set) {
        route.g.classList.remove('is-winner');
        route.set.classList.remove('is-winner');
        refreshFrames(route);
      }
    });
  }
  function scramble(on: boolean) {
    window.clearInterval(scrambleT);
    intervals.delete(scrambleT);
    scrambleT = 0;
    ROUTES.forEach((route) => {
      if (route.tw) {
        cancelAnimationFrame(route.tw);
        Object.assign(route, { tw: 0 });
      }
    });
    if (!on) return;
    if (reduced.matches) {
      ROUTES.forEach((route) => Object.assign(route.msEl, { textContent: '··' }));
      return;
    }
    const tick = () => ROUTES.forEach((route) => Object.assign(route.msEl, { textContent: String(2 + Math.floor(Math.random() * 40)) }));
    tick();
    scrambleT = window.setInterval(tick, 90);
    intervals.add(scrambleT);
  }
  function revealScenario(sc: Scenario, amp: number, done: () => void) {
    setBad(sc.bad ?? BAD_MS, 0);
    ROUTES.forEach((route) => {
      Object.assign(route, { lat: jitter(sc[route.id] ?? route.lat, amp) });
      route.msEl.textContent = '··';
    });
    if (running) setRates();
    const finish = () => {
      ROUTES.forEach((route) => route.msWrap.classList.remove('is-set'));
      done();
    };
    if (reduced.matches) {
      ROUTES.forEach((route) => {
        setShown(route, route.lat);
        pushReading(route);
      });
      finish();
      return;
    }
    ROUTES.forEach((route, index) => {
      later(
        () => {
          setShown(route, route.lat);
          pushReading(route);
          route.msWrap.classList.add('is-set');
          flip(route.msWrap);
        },
        40 + index * 60
      );
    });
    later(finish, 40 + ROUTES.length * 60 + 160);
  }
  function statusFor(res: WinnerResult, ctx: string) {
    const { best } = res;
    if (res.changed && res.prev) return strings.statusChanged(cityHtml(res.prev), stMs(res.prev), cityHtml(best), stMs(best));
    if (ctx === 'measured') return strings.statusMeasured(cityHtml(best), stMs(best));
    return strings.statusLeading(cityHtml(best), stMs(best));
  }
  function swapStatus(html: string, fade: boolean) {
    if (statusText!.innerHTML === html) return;
    statusText!.classList.remove('is-swap');
    if (fade && !reduced.matches) {
      reflow(statusText!);
      statusText!.innerHTML = html;
      statusText!.classList.add('is-swap');
      return;
    }
    statusText!.innerHTML = html;
  }
  function describeFocus() {
    const route = byId(focusId);
    const best = byId(winnerId);
    if (!route || !best) return;
    if (route.id === winnerId) {
      statusText!.innerHTML = strings.statusFocusWinner(cityHtml(route), stMs(route));
      return;
    }
    const describe = rankOf(route) === 2 ? strings.statusFocusNext : strings.statusFocusReserve;
    statusText!.innerHTML = describe(cityHtml(route), stDelta(route, best), escapeHtml(best.city));
  }
  function announce(res: { best: Route; changed: boolean; prev?: Route | null }, ctx: string) {
    if (focusId) {
      describeFocus();
      return;
    }
    const full: WinnerResult = { best: res.best, changed: res.changed, prev: res.prev ?? null };
    swapStatus(paused ? strings.statusPaused(cityHtml(res.best), stMs(res.best)) : statusFor(full, ctx), ctx === 'swap' && res.changed);
  }
  function live(message: string) {
    liveEl!.textContent = '';
    liveEl!.textContent = message;
  }
  const engaged = () => !!(pinned || focusId || list!.contains(document.activeElement));

  function retest(fromUser: boolean) {
    if (testing) return;
    testing = true;
    scenario = (scenario + 1) % SCENARIOS.length;
    stage!.classList.add('is-testing');
    proofEl!.classList.add('is-measuring');
    uncrown();
    scramble(true);
    ROUTES.forEach((route) => route.el.setAttribute('aria-label', strings.routeLabelMeasuring(route.city, route.country)));
    directEl!.setAttribute('aria-label', strings.directLabelMeasuring);
    btn!.classList.add('is-testing');
    btn!.setAttribute('aria-disabled', 'true');
    btn!.setAttribute('aria-busy', 'true');
    btnLabel!.textContent = strings.buttonBusy;
    statusText!.textContent = strings.statusMeasuring;
    if (fromUser) live(strings.liveMeasuring);
    boost = 2.4;
    if (running) setRates();
    later(
      () => {
        scramble(false);
        revealScenario(SCENARIOS[scenario]!, 1, () => {
          stage!.classList.remove('is-testing');
          const res = applyWinner(false);
          proofEl!.classList.remove('is-measuring');
          btn!.classList.remove('is-testing');
          btn!.removeAttribute('aria-disabled');
          btn!.setAttribute('aria-busy', 'false');
          btnLabel!.textContent = strings.buttonIdle;
          boost = 1;
          if (running) setRates();
          testing = false;
          reaim(res);
          announce(res, fromUser ? 'measured' : '');
          if (fromUser) {
            bridge?.classList.add('is-hot');
            const pct = pctFor(res.best.lat);
            live(res.changed && res.prev ? strings.liveMeasuredChanged(res.best.city, res.best.lat, res.prev.city, pct) : strings.liveMeasured(res.best.city, res.best.lat, pct));
          }
        });
      },
      reduced.matches ? 80 : 900
    );
  }

  let loopN = 0;
  function loop() {
    later(() => {
      if (built && visible && !document.hidden && !reduced.matches && !testing && !paused && !engaged()) {
        loopN += 1;
        if (loopN % 2 === 0) {
          scenario = (scenario + 1) % SCENARIOS.length;
          announce(applyScenario(SCENARIOS[scenario]!, 1, true), 'swap');
        } else announce(applyScenario(SCENARIOS[scenario]!, 3, false), '');
      }
      loop();
    }, LOOP_MS);
  }
  // live readings: every half second the figures move within their bands (winner 2–5 ms, the rest above it,
  // the direct line 180–250 ms) without changing who leads; handovers stay on loop()
  function livePing() {
    later(() => {
      if (built && visible && !document.hidden && !reduced.matches && !testing && !paused && winnerId) {
        const winner = byId(winnerId)!;
        const sc = SCENARIOS[scenario]!;
        winner.lat = rint(2, 5);
        ROUTES.forEach((route) => {
          if (route !== winner) Object.assign(route, { lat: Math.max(winner.lat + 1, jitter(sc[route.id] ?? route.lat, 1)) });
          if (route.tw) {
            cancelAnimationFrame(route.tw);
            Object.assign(route, { tw: 0 });
          }
          setShown(route, route.lat);
          pushReading(route);
        });
        setBad(rint(BAD_LO, BAD_HI), 0);
        syncDerived();
        setProof(winner.lat);
      }
      livePing();
    }, LIVE_MS);
  }
  livePing();

  /* ---- interactions */
  let hintGone = false;
  function dismissHint() {
    if (hintGone) return;
    hintGone = true;
    if (reduced.matches) {
      hintText!.hidden = true;
      return;
    }
    hintText!.classList.add('is-gone');
    later(() => {
      hintText!.hidden = true;
    }, 450);
  }
  function setFocus(id: string | null, sticky?: boolean) {
    if (id) dismissHint();
    focusId = id;
    stage!.classList.toggle('has-focus', !!id);
    ROUTES.forEach((route) => {
      const on = route.id === id;
      route.el.classList.toggle('is-focus', on);
      if (route.g && route.set) {
        route.g.classList.toggle('is-focus', on);
        route.set.classList.toggle('is-focus', on);
      }
      if (sticky !== undefined) route.el.setAttribute('aria-pressed', on && sticky ? 'true' : 'false');
    });
    if (id) describeFocus();
    else if (winnerId) announce({ best: byId(winnerId)!, changed: false }, '');
  }
  function setPin(id: string | null) {
    pinned = id;
    ROUTES.forEach((route) => route.el.classList.toggle('is-pinned', route.id === id));
    root.classList.toggle('has-pin', !!id);
    if (id) {
      hintGone = true;
      hintText!.classList.add('is-gone');
      hintText!.hidden = true;
    }
    clearBtn!.hidden = !id;
  }
  function clearPin(returnFocusTo?: HTMLElement | null) {
    const had = !!(pinned || focusId);
    setPin(null);
    setFocus(null, false);
    if (had) live(strings.liveCleared);
    if (returnFocusTo) {
      try {
        returnFocusTo.focus({ preventScroll: true });
      } catch {
        /* focus is best effort */
      }
    }
  }
  const routeOf = (event: Event) => (event.target instanceof Element ? event.target.closest<HTMLElement>('[data-route]') : null);

  listen<PointerEvent>(list, 'pointerover', (event) => {
    const button = routeOf(event);
    if (!button || pinned || event.pointerType === 'touch') return;
    setFocus(button.getAttribute('data-route'));
  });
  listen<PointerEvent>(list, 'pointerout', (event) => {
    const button = routeOf(event);
    if (!button || pinned || event.pointerType === 'touch') return;
    if (event.relatedTarget instanceof Node && button.contains(event.relatedTarget)) return;
    setFocus(null);
  });
  listen<MouseEvent>(list, 'click', (event) => {
    const button = routeOf(event);
    if (!button) return;
    const id = button.getAttribute('data-route');
    if (pinned === id) {
      clearPin();
      return;
    }
    setPin(id);
    setFocus(id, true);
    if (event.detail === 0) {
      try {
        clearBtn!.focus({ preventScroll: true });
      } catch {
        /* focus is best effort */
      }
    }
    const route = byId(id);
    const winner = byId(winnerId);
    if (!route || !winner) return;
    live(id === winnerId ? strings.livePinnedWinner(route.city, route.lat) : strings.livePinned(route.city, route.lat, route.lat - winner.lat, winner.city));
  });
  listen<FocusEvent>(list, 'focusin', (event) => {
    const button = routeOf(event);
    if (!button || pinned) return;
    setFocus(button.getAttribute('data-route'));
  });
  listen<FocusEvent>(list, 'focusout', (event) => {
    if (pinned) return;
    if (!(event.relatedTarget instanceof Node) || !list!.contains(event.relatedTarget)) setFocus(null);
  });
  listen<MouseEvent>(stage, 'click', (event) => {
    if (pinned && !(event.target instanceof Element && event.target.closest('[data-route]'))) clearPin();
  });
  listen<MouseEvent>(clearBtn, 'click', (event) => {
    const to = pinned && event.detail === 0 ? (byId(pinned)?.el ?? null) : null;
    clearPin(to);
  });
  listen<KeyboardEvent>(root, 'keydown', (event) => {
    if (event.key === 'Escape' && (pinned || focusId)) clearPin();
  });
  listen(btn, 'click', () => {
    if (btn!.getAttribute('aria-disabled') === 'true') return;
    retest(true);
  });
  listen(pauseBtn, 'click', () => {
    paused = !paused;
    root.classList.toggle('is-paused', paused);
    pauseBtn!.setAttribute('aria-pressed', paused ? 'true' : 'false');
    if (paused) stop();
    else syncRun();
    if (winnerId) announce({ best: byId(winnerId)!, changed: false }, '');
    live(paused ? strings.livePaused : strings.liveResumed);
  });

  function syncRun() {
    if (destroyed) return;
    const on = visible && !document.hidden;
    root.classList.toggle('is-idle', !on);
    if (on) {
      armMotion();
      start();
    } else stop();
  }

  const observers: Array<{ disconnect: () => void }> = [];
  if (typeof IntersectionObserver === 'function') {
    const buildObserver = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0]?.isIntersecting) {
          buildStage();
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px' }
    );
    buildObserver.observe(stage);
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        visible = !!entries[0]?.isIntersecting;
        syncRun();
      },
      { threshold: 0.05 }
    );
    visibilityObserver.observe(stage);
    observers.push(buildObserver, visibilityObserver);
    later(() => {
      if (built || document.hidden) return;
      const rect = stage.getBoundingClientRect();
      if (rect.bottom > -320 && rect.top < window.innerHeight + 320) buildStage();
    }, 1500);
  } else {
    visible = true;
    armMotion();
  }
  listen(document, 'visibilitychange', syncRun);
  let resizeRaf = 0;
  const scheduleLayout = () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => layout(false));
  };
  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(scheduleLayout);
    resizeObserver.observe(stage);
    observers.push(resizeObserver);
  } else listen(window, 'resize', scheduleLayout);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => {
      if (!destroyed) scheduleLayout();
    });
  }
  const onReducedChange = () => {
    if (reduced.matches) {
      stop();
      drawStatic();
    } else syncRun();
  };
  reduced.addEventListener?.('change', onReducedChange);
  cleanups.push(() => reduced.removeEventListener?.('change', onReducedChange));

  announce(applyWinner(false), '');
  loop();

  return () => {
    destroyed = true;
    timeouts.forEach((id) => window.clearTimeout(id));
    intervals.forEach((id) => window.clearInterval(id));
    cancelAnimationFrame(motionRaf);
    cancelAnimationFrame(resizeRaf);
    cancelAnimationFrame(axis.raf);
    ROUTES.forEach((route) => cancelAnimationFrame(route.tw));
    observers.forEach((observer) => observer.disconnect());
    cleanups.forEach((cleanup) => cleanup());
    eachAnim((animation) => animation.cancel());
    // everything the engine built goes; the React markup gets back its engine-free classes
    svg.replaceChildren();
    pkLayer.replaceChildren();
    root.classList.remove('is-js', 'is-live', 'is-idle', 'is-paused', 'has-pin');
    stage.classList.remove('is-ready', 'is-motion', 'is-testing', 'is-aiming', 'has-focus');
    ROUTES.forEach((route) => route.el.classList.remove('is-focus', 'is-pinned', 'is-burst'));
  };
}
